<template>
	<ChalkyUtilityModal
		class="view-modal-global-addphoto default-modal"
		v-bind:class="stateClass"
		v-model="isVisible"
		v-on:hide="Handle_OnHide"
	>
		<ChalkyUtilitySlideshow
			ref="slideshowView"
			v-bind:showDots="false"
			v-bind:showNavigation="false"
			v-bind:slideCount="3"
			v-on:change="Handle_OnSlideChange"
		>
			<!-- Slide 0: Select Photo -->
			<template #slide-0>
				<header class="stepped-overlay-header">
					<h3>Add a Photo</h3>
					<p>Share a moment from your session</p>
				</header>

				<section class="primary-container stepped-overlay-content">
					<section class="upload-container">
						<ChalkyFormMediaUploader
							ref="formMediaUploader"
							type="image"
							v-bind:allowMultiple="false"
							v-bind:model="userModel"
							v-bind:showDefaultActions="false"
							v-bind:thickness="2"
							v-on:complete="Handle_OnImageUploaded"
							v-on:files="Handle_OnImageFiles"
							v-on:upload-error="Handle_OnImageUploadError"
							v-on:upload-start="Handle_OnImageUploadStart"
							variant="avatar"
						/>
					</section>

					<!-- Venue tagging -->
					<section class="venue-container">
						<label>
							<h6>Where was this taken?</h6>

							<ChalkyVenueRow v-if="selectedVenueModel" v-bind:venueModel="selectedVenueModel">
								<template v-slot:actions>
									<button
										class="control round size-medium theme-dark type-close"
										v-on:click="Handle_OnClickClearVenue"
									>
										Clear
									</button>
								</template>
							</ChalkyVenueRow>

							<ChalkyFormSearch
								class="size-small"
								placeholder="Search for a place"
								v-bind:autocomplete="true"
								v-bind:autocompleteDelay="300"
								v-bind:type="'venue'"
								v-bind:venueCollection="venueCollection"
								v-else
								v-on:autocomplete:select="Handle_OnSelectVenue"
							/>

							<small>Optional</small>
						</label>
					</section>

					<!-- User tagging -->
					<!-- <section class="user-container">
						<label>
							<h6>Who are you with?</h6>

							<ChalkyUserRow v-if="selectedUserModel" v-bind:userModel="selectedUserModel">
								<template v-slot:actions>
									<button
										class="control round size-medium theme-dark type-close"
										v-on:click="Handle_OnClickClearUser"
									>
										Clear
									</button>
								</template>
							</ChalkyUserRow>

							<ChalkyFormSearch
								class="size-small"
								placeholder="Search for a person"
								v-bind:autocomplete="true"
								v-bind:autocompleteDelay="300"
								v-bind:type="'user'"
								v-bind:userCollection="userCollection"
								v-else
								v-on:autocomplete:select="Handle_OnSelectUser"
							/>

							<small>Optional</small>
						</label>
					</section> -->
				</section>

				<section class="action-container stepped-overlay-actions">
					<div class="button-group">
						<button
							class="button-primary size-medium"
							v-bind:disabled="isLoading || fileCount === 0"
							v-on:click="Handle_OnClickSubmit"
						>
							<span v-if="isLoading">Uploading...</span>
							<span v-else>Upload</span>
						</button>
					</div>

					<p class="footnote stepped-overlay-footnote">
						By uploading this image, you agree to our
						<br />
						<a href="https://www.chalkysticks.com/terms-of-use" target="_blank">terms of service</a>
						and
						<a href="https://www.chalkysticks.com/privacy-policy" target="_blank">privacy policy</a>
						.
					</p>
				</section>
			</template>

			<!-- Slide 2: Confirmation -->
			<template #slide-2>
				<section class="primary-container stepped-overlay-content">
					<section class="complete-container">
						<picture
							v-for="mediaModel in uploadedMedia"
							v-bind:key="mediaModel.id"
							v-bind:style="{
								width: uploadedMedia.length > 1 ? '120px' : null,
							}"
						>
							<img
								class="icon filter-invert"
								src="~@chalkysticks/sass/build/asset/image/icon/checkmark.svg"
							/>
							<img v-bind:src="mediaModel.getUrlMedium()" />
						</picture>
					</section>

					<section class="content stepped-overlay-complete">
						<h3>Success!</h3>

						<p>
							Your
							<span v-if="uploadedMedia.length > 1">images have been uploaded</span>
							<span v-else>image has been uploaded</span>
							and you may start to see
							<span v-if="uploadedMedia.length > 1">them</span>
							<span v-else>it</span>
							on the wall within the hour
						</p>
					</section>
				</section>

				<section class="action-container stepped-overlay-actions">
					<div class="button-group">
						<button class="button-primary size-medium" v-on:click="Handle_OnClickClose">Close</button>
					</div>
				</section>
			</template>
		</ChalkyUtilitySlideshow>
	</ChalkyUtilityModal>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Ref } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * Global modal for uploading photos.
	 * Three steps: Select Photo → Tag Content → Confirmation
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Modal/Global
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewModalGlobalAddPhoto extends ViewBase {
		/**
		 * CSS state class based on the current status of the photo upload flow.
		 *
		 * @return string
		 */
		public get stateClass(): string {
			if (this.uploadedMedia.length > 0) {
				return 'state-complete';
			}

			if (this.isLoading) {
				return 'state-uploading';
			}

			return 'state-selection';
		}

		/**
		 * @type ChalkySticks/Model/User
		 */
		protected get userModel(): ChalkySticks.Model.User {
			return this.$store.getters['authentication/user'];
		}

		/**
		 * Reference to the form media uploader component
		 *
		 * @type any
		 */
		@Ref('formMediaUploader')
		readonly formMediaUploader!: any;

		/**
		 * Reference to the slideshow view for navigation
		 *
		 * @type any
		 */
		@Ref('slideshowView')
		protected slideshowView!: any;

		/**
		 * Number of files selected
		 *
		 * @type number
		 */
		protected fileCount: number = 0;

		/**
		 * Whether a request is in progress
		 *
		 * @type boolean
		 */
		protected isLoading: boolean = false;

		/**
		 * Whether the modal is visible
		 *
		 * @type boolean
		 */
		protected isVisible: boolean = false;

		/**
		 * The selected user model for tagging
		 *
		 * @type ChalkySticks.Model.User | null
		 */
		protected selectedUserModel: ChalkySticks.Model.User | null = null;

		/**
		 * The selected venue model for tagging
		 *
		 * @type ChalkySticks.Model.Venue | null
		 */
		protected selectedVenueModel: ChalkySticks.Model.Venue | null = null;

		/**
		 * Successfully uploaded media models
		 *
		 * @type ChalkySticks.Model.Media[]
		 */
		protected uploadedMedia: ChalkySticks.Model.Media[] = [];

		/**
		 * Collection for user search autocomplete
		 *
		 * @type ChalkySticks.Collection.User
		 */
		protected userCollection: ChalkySticks.Collection.User = ChalkySticks.Factory.User.collection({
			limit: 12,
		});

		/**
		 * Collection for venue search autocomplete
		 *
		 * @type ChalkySticks.Collection.Venue
		 */
		protected venueCollection: ChalkySticks.Collection.Venue = ChalkySticks.Factory.Venue.collection({
			limit: 8,
		});

		// region: Lifecycle
		// ---------------------------------------------------------------------------

		/**
		 * Attach event listeners on mount.
		 *
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			ChalkySticks.Core.Event.Bus.on('modal:photo:open', this.Handle_OnOpen);
		}

		/**
		 * Detach event listeners before destroy.
		 *
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			ChalkySticks.Core.Event.Bus.off('modal:photo:open', this.Handle_OnOpen);
		}

		// endregion: Lifecycle

		// region: Actions
		// ---------------------------------------------------------------------------

		/**
		 * Close the modal and reset state.
		 *
		 * @return void
		 */
		protected close(): void {
			this.isVisible = false;
			this.reset();
		}

		/**
		 * Open the modal.
		 *
		 * @return void
		 */
		protected open(): void {
			this.reset();
			this.isVisible = true;
		}

		/**
		 * Reset modal state to initial values.
		 *
		 * @return void
		 */
		protected reset(): void {
			this.fileCount = 0;
			this.isLoading = false;
			this.selectedUserModel = null;
			this.selectedVenueModel = null;
			this.uploadedMedia = [];
		}

		// endregion: Actions

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnOpen(): Promise<void> {
			this.open();
		}

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnHide(): Promise<void> {
			this.close();
		}

		/**
		 * Handle close button click.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickClose(e?: PointerEvent): Promise<void> {
			e?.preventDefault();

			this.close();
		}

		/**
		 * Handle continue button click - advance to next slide.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickContinue(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.slideshowView.next();
		}

		/**
		 * Handle slideshow slide change.
		 *
		 * @param number index
		 * @return Promise<void>
		 */
		protected async Handle_OnSlideChange(index: number): Promise<void> {
			if (index === 0) {
				this.close();
			}
		}

		/**
		 * Handle file selection from uploader.
		 *
		 * @param number count
		 * @return Promise<void>
		 */
		protected async Handle_OnImageFiles(count: number): Promise<void> {
			this.fileCount = count;
		}

		/**
		 * Handle image upload start.
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnImageUploadStart(): Promise<void> {
			this.isLoading = true;
		}

		/**
		 * Handle image upload complete.
		 *
		 * @param ChalkySticks.Model.Media[] mediaModels
		 * @return Promise<void>
		 */
		protected async Handle_OnImageUploaded(mediaModels: ChalkySticks.Model.Media[]): Promise<void> {
			this.uploadedMedia = mediaModels;
			this.isLoading = false;
			this.slideshowView.next();

			// Dispatch success event
			ChalkySticks.Core.Event.Bus.dispatch('photo:upload:complete', {
				mediaModels: this.uploadedMedia,
			});
		}

		/**
		 * Handle image upload error.
		 *
		 * @param any error
		 * @return Promise<void>
		 */
		protected async Handle_OnImageUploadError(error: any): Promise<void> {
			this.isLoading = false;
			ChalkySticks.Utility.Log.error('photo', 'Upload error:', error);

			ChalkySticks.Core.Event.Bus.dispatch('dialog:alert', {
				message: 'Failed to upload photo. Please try again.',
				title: 'Error',
			});
		}

		/**
		 * Handle venue selection from search.
		 *
		 * @param ChalkySticks.Model.Venue venueModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectVenue(venueModel: ChalkySticks.Model.Venue): Promise<void> {
			this.selectedVenueModel = venueModel;
		}

		/**
		 * Handle clear venue button click.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickClearVenue(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.selectedVenueModel = null;
		}

		/**
		 * Handle user selection from search.
		 *
		 * @param ChalkySticks.Model.User userModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectUser(userModel: ChalkySticks.Model.User): Promise<void> {
			this.selectedUserModel = userModel;
		}

		/**
		 * Handle clear user button click.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickClearUser(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.selectedUserModel = null;
		}

		/**
		 * Handle submit button click - upload and tag.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickSubmit(e: PointerEvent): Promise<void> {
			e.preventDefault();

			if (this.isLoading) {
				return;
			}

			// Not logged in
			if (!this.$store || !this.$store.getters['authentication/authenticated']) {
				ChalkySticks.Core.Event.Bus.dispatch('login:request');
				return;
			}

			this.isLoading = true;

			try {
				// Upload media
				const mediaModels: ChalkySticks.Model.Media[] = await this.formMediaUploader.upload();
				const tagPromises: Promise<ChalkySticks.Model.MediaTag>[] = [];

				// Tag user and venue in parallel
				for (const mediaModel of mediaModels) {
					if (this.selectedUserModel) {
						tagPromises.push(mediaModel.tag(this.selectedUserModel));
					}

					if (this.selectedVenueModel) {
						tagPromises.push(mediaModel.tag(this.selectedVenueModel));
					}
				}

				await Promise.all(tagPromises);

				this.uploadedMedia = mediaModels;
				this.slideshowView.next();

				// Dispatch success event
				ChalkySticks.Core.Event.Bus.dispatch('photo:upload:complete', {
					mediaModels: this.uploadedMedia,
				});
			} catch (error) {
				ChalkySticks.Utility.Log.error('photo', 'Failed to upload:', error);

				ChalkySticks.Core.Event.Bus.dispatch('dialog:alert', {
					message: 'Failed to upload photo. Please try again.',
					title: 'Error',
				});
			} finally {
				this.isLoading = false;
			}
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-modal-global-addphoto {
		.upload-container {
			min-height: 150px;
		}

		.user-container,
		.venue-container {
			h5 {
				margin-bottom: 0.75rem;
			}
		}

		.complete-container {
			display: flex;
			flex-wrap: wrap;
			gap: 1rem;
			justify-content: center;

			picture {
				border-radius: 0.5rem;
				max-width: 200px;
				overflow: hidden;
				position: relative;

				.icon {
					height: 24px;
					position: absolute;
					right: 0.5rem;
					top: 0.5rem;
					width: 24px;
				}

				img:not(.icon) {
					display: block;
					height: auto;
					width: 100%;
				}
			}
		}
	}

</style>
